import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import TargetDomainForm from 'routes/TargetDomain/components/TargetDomainForm';
import { Ref } from 'routes/TargetDomain/components/TargetDomainForm/TargetDomainForm';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as targetDomainActions from 'data/targetDomain/targetDomain.actions';
import { TargetDomain } from 'data/targetDomain/targetDomain.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newTargetDomain } from 'routes/TargetDomain/TargetDomain.types';
import { targetDomainTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFormData,
  adaptTargetDomainTemplate,
} from '../../utils/targetDomainTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/targetDomainTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  targetDomainTemplateDef: adaptTemplateVisibilityMode(
    targetDomainTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
});

const mapDispatch = {
  createTargetDomain: targetDomainActions.createTargetDomain,
  goToTargetDomainList: targetDomainActions.goToTargetDomainList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const TargetDomainCreate = ({
  history,
  userId,
  deliveryFunctionId,
  createTargetDomain,
  userName,
  isAuditEnabled,
  targetDomainTemplateDef,
  goToTargetDomainList,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<TargetDomain>(
    cloneDeep(newTargetDomain),
  );
  const [template, setTemplate] = useState(targetDomainTemplateDef);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as TargetDomain);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptTargetDomainTemplate(
      clonedTemplate,
    );

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateTargetDomainActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const targetDomainFormData = formRef.current.getValues();
    const payload = {
      data: targetDomainFormData,
      auditDetails: getAuditDetailsToSubmit(
        targetDomainFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateTargetDomainActionPayload();
    if (payload) {
      createTargetDomain(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToTargetDomainList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <TargetDomainForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="TargetDomainCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('targetDomain.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(TargetDomainCreate));
